import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import LiveBoardCoachEditor from './LiveBoardCoachEditor';
import ExerciseHistoryModal from './ExerciseHistoryModal';
import { Pencil } from 'lucide-react';
import {
  getCurrentSetProgress,
  getNextExercise,
  formatRestTimer,
  formatRestTarget,
  getDisplayState,
  formatSetDisplay,
  formatRoundDisplay,
  formatTimeDuration
} from '../utils/liveBoardHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAnimationUrl = (gifUrl) => {
  if (!gifUrl) return null;
  if (gifUrl.startsWith('/media/')) return `${BACKEND_URL}/api${gifUrl}`;
  if (gifUrl.startsWith('/')) return `${BACKEND_URL}${gifUrl}`;
  return gifUrl;
};

const LiveGymBoard = () => {
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [timeSinceLastSet, setTimeSinceLastSet] = useState({});
  const [currentTime, setCurrentTime] = useState(new Date());
  const [editing, setEditing] = useState(null); // { workoutId, clientName }
  const [historyView, setHistoryView] = useState(null); // { exerciseId, exerciseName, clientId, clientName }

  const fetchLiveBoard = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) { setError('Not authenticated'); setLoading(false); return; }
      const response = await axios.get(`${API}/live-board`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkouts(response.data.workouts || []);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error('Error fetching live board:', err);
      if (err.response?.status === 401) setError('Session expired. Please login again.');
      else setError('Failed to load live board');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchLiveBoard();
    const interval = setInterval(fetchLiveBoard, 2000);
    return () => clearInterval(interval);
  }, [fetchLiveBoard]);

  // Real-time clock + time since last set (both tick every second)
  useEffect(() => {
    const tick = () => {
      setCurrentTime(new Date());
      const newTimes = {};
      workouts.forEach(workout => {
        if (workout.last_set_timestamp) {
          try {
            const diff = Math.floor((Date.now() - new Date(workout.last_set_timestamp).getTime()) / 1000);
            if (diff >= 0) newTimes[workout.workout_id] = diff;
          } catch { /* invalid timestamp */ }
        }
      });
      setTimeSinceLastSet(newTimes);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [workouts]);

  const getStateColors = (state) => {
    switch (state) {
      case 'READY': return { bg: 'bg-green-900/40', border: 'border-green-500', text: 'text-green-400', glow: 'shadow-green-500/30', headerBg: 'bg-green-900/60' };
      case 'RESTING': return { bg: 'bg-yellow-900/40', border: 'border-yellow-500', text: 'text-yellow-400', glow: 'shadow-yellow-500/30', headerBg: 'bg-yellow-900/60' };
      case 'ACTIVE': return { bg: 'bg-blue-900/40', border: 'border-blue-500', text: 'text-blue-400', glow: 'shadow-blue-500/30', headerBg: 'bg-blue-900/60' };
      default: return { bg: 'bg-gray-900/40', border: 'border-gray-500', text: 'text-gray-400', glow: '', headerBg: 'bg-gray-900/60' };
    }
  };

  const renderSetIcon = (status) => {
    switch (status) {
      case 'completed': return <span className="text-green-400 text-lg">✓</span>;
      case 'last_performed':
      case 'current': return <span className="text-yellow-400 text-lg">●</span>;
      default: return <span className="text-gray-500 text-lg">○</span>;
    }
  };

  const renderProgressBar = (progress) => {
    // Smooth bar — matches the intra-workout interface's rest panel styling.
    const pct = Math.max(0, Math.min(100, progress));
    return (
      <div className="w-full bg-black/30 rounded-full h-2 mt-2 overflow-hidden">
        <div
          className="bg-yellow-400 h-2 rounded-full transition-all duration-1000"
          style={{ width: `${pct}%` }}
        />
      </div>
    );
  };

  const formatTimeSince = (seconds) => {
    if (typeof seconds !== 'number' || seconds < 0) return null;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Render a single exercise mini-card (used inside superset block)
  const renderExerciseMiniCard = (ssEx, isActive, workout) => {
    const completedSets = ssEx.completed_sets || 0;
    const totalSets = ssEx.total_sets || 3;
    const currentSet = Math.min(completedSets + 1, totalSets);
    const isCardio = !!ssEx.is_cardio;

    return (
      <div
        key={ssEx.index}
        className={`rounded-xl p-2.5 transition-all ${
          isActive
            ? 'bg-orange-500/20 border-2 border-orange-400 shadow-lg shadow-orange-500/20'
            : 'bg-gray-800/40 border border-gray-700/50'
        }`}
      >
        <div className="flex gap-2 items-start">
          {/* GIF */}
          <div className="flex-shrink-0 w-14 h-14 bg-black/30 rounded-lg overflow-hidden">
            {ssEx.exercise_gif ? (
              <img src={getAnimationUrl(ssEx.exercise_gif)} alt={ssEx.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-2xl">{isCardio ? '🏃' : '🏋️'}</div>
            )}
          </div>
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              {isActive && <span className="text-orange-400 text-xs font-bold">▶</span>}
              {ssEx.exercise_id && workout ? (
                <button
                  type="button"
                  onClick={() => setHistoryView({ exerciseId: ssEx.exercise_id, exerciseName: ssEx.name, clientId: workout.client_id, clientName: workout.client_name })}
                  className={`text-sm font-bold truncate text-left hover:text-cyan-300 hover:underline decoration-dotted underline-offset-4 transition-colors cursor-pointer ${isActive ? 'text-white' : 'text-gray-300'}`}
                  title="View this client's history for this exercise"
                >
                  {ssEx.name}
                </button>
              ) : (
                <h4 className={`text-sm font-bold truncate ${isActive ? 'text-white' : 'text-gray-300'}`}>
                  {ssEx.name}
                </h4>
              )}
            </div>
            <p className={`text-xs font-semibold ${isActive ? 'text-orange-300' : 'text-gray-500'}`}>
              {isCardio ? 'Ronda' : 'Set'} {currentSet} / {totalSets}
              {isCardio && ssEx.cardio_total_time > 0 && (
                <span className="ml-1 text-cyan-300/70 font-mono">· {formatTimeDuration(ssEx.cardio_total_time)}</span>
              )}
            </p>
          </div>
        </div>
        {/* Compact sets grid */}
        {ssEx.sets && ssEx.sets.length > 0 && (
          <div className="flex gap-1 mt-2 flex-wrap">
            {ssEx.sets.map((set, idx) => (
              <div
                key={idx}
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${
                  set.status === 'current'
                    ? 'bg-yellow-500/30 border border-yellow-500 text-white'
                    : set.status === 'completed'
                    ? (isCardio ? 'bg-cyan-500/20 border border-cyan-500/40 text-white' : 'bg-green-500/20 border border-green-500/30 text-white')
                    : 'bg-gray-800/50 border border-gray-700/50 text-gray-500'
                }`}
              >
                {isCardio ? (
                  <span className="font-mono">
                    {set.status === 'current' && (!set.time || set.time === 0) ? '⏱' : formatRoundDisplay(set)}
                  </span>
                ) : (
                  <>
                    <span className="font-mono">{formatSetDisplay(set)}kg</span>
                    {set.rpe != null && (
                      <span className={`font-bold ${
                        set.rpe <= 5 ? 'text-green-400' : set.rpe <= 7 ? 'text-yellow-400' : set.rpe <= 8 ? 'text-orange-400' : 'text-red-400'
                      }`}>R{set.rpe}</span>
                    )}
                  </>
                )}
                {renderSetIcon(set.status)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderColumn = (workout) => {
    const displayState = getDisplayState(workout.state, workout.rest_timer);
    const colors = getStateColors(displayState);
    const exercise = workout.current_exercise;
    const isResting = displayState === 'RESTING' && workout.rest_timer;
    const secondsSinceLastSet = timeSinceLastSet[workout.workout_id];
    const isSuperset = exercise?.superset && exercise.superset.exercises?.length === 2;

    // Live cardio stopwatch: elapsed = base_seconds + (now - started_at) while
    // the client's cardio timer is running. Guarded by exercise_index so a
    // stale "running" flag from a previous exercise never leaks in.
    const cl = workout.cardio_live;
    let liveCardioSeconds = null;
    if (
      exercise?.is_cardio && cl?.running && cl?.started_at &&
      (cl.exercise_index == null || cl.exercise_index === workout.exercise_index - 1)
    ) {
      try {
        const delta = Math.max(0, Math.floor((currentTime.getTime() - new Date(cl.started_at).getTime()) / 1000));
        liveCardioSeconds = (cl.base_seconds || 0) + delta;
      } catch { liveCardioSeconds = null; }
    }
    const liveCardioDisplay = liveCardioSeconds != null
      ? `${Math.floor(liveCardioSeconds / 60)}:${(liveCardioSeconds % 60).toString().padStart(2, '0')}`
      : null;

    // For "Next" label: if in superset, show next exercise after the superset block
    const exercises = workout.exercises || [];
    const currentIndex = workout.exercise_index - 1;
    let nextExercise = null;
    if (isSuperset) {
      // Next is the exercise after the superset pair
      const lastSsIdx = Math.max(...exercise.superset.exercises.map(e => e.index));
      if (lastSsIdx + 1 < exercises.length) {
        nextExercise = exercises[lastSsIdx + 1];
      }
    } else {
      nextExercise = getNextExercise(exercises, currentIndex);
    }

    return (
      <div
        key={workout.workout_id}
        className={`flex flex-col rounded-2xl border-4 ${colors.border} ${colors.bg} shadow-2xl ${colors.glow} overflow-hidden transition-all duration-300`}
        data-testid={`workout-column-${workout.workout_id}`}
        style={{ maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className={`p-3 ${colors.headerBg} border-b-2 ${colors.border}`}>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 flex-1 min-w-0 mr-2">
              <h2 className="text-white text-xl font-bold truncate">{workout.client_name}</h2>
              {workout.mode === 'flexible' && (
                <span
                  className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/30 text-amber-100 border border-amber-300/60 font-semibold flex-shrink-0"
                  title="Rutina libre — sin cita"
                  data-testid={`flexible-pill-${workout.workout_id}`}
                >
                  🏠 Libre
                </span>
              )}
            </div>
            <div className="flex flex-col items-end flex-shrink-0">
              <span className="text-white text-lg font-mono font-bold">{workout.elapsed_time}</span>
              {workout.est_remaining_seconds > 0 && workout.state !== 'COMPLETED' && (
                <span
                  className="text-amber-200/55 text-[11px] font-medium mt-0.5 whitespace-nowrap"
                  title="Estimated time remaining (adaptive average)"
                  data-testid={`est-remaining-${workout.workout_id}`}
                >
                  ⏱ ~{Math.round(workout.est_remaining_seconds / 60)} min restante
                </span>
              )}
            </div>
          </div>
          <div className="mt-1.5 flex justify-end">
            <button
              onClick={() => setEditing({ workoutId: workout.workout_id, clientName: workout.client_name })}
              className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-orange-500/25 text-orange-100 border border-orange-300/50 hover:bg-orange-500/40 transition"
              data-testid={`coach-edit-btn-${workout.workout_id}`}
            >
              <Pencil className="w-3 h-3" /> Coach edit
            </button>
          </div>
        </div>

        {/* Exercise content */}
        {exercise && (
          <div className="flex-1 flex flex-col p-3 overflow-hidden">
            
            {/* ===== SUPERSET BLOCK ===== */}
            {isSuperset ? (
              <div className="flex-1 flex flex-col">
                {/* Superset header */}
                <div className="mb-2 px-3 py-1.5 bg-orange-500/15 border border-orange-500/40 rounded-lg flex items-center justify-between">
                  <p className="text-orange-400 text-sm font-bold uppercase tracking-wider">
                    Superset {exercise.superset.group}
                  </p>
                  <span className="text-orange-300/60 text-xs">
                    {exercise.superset.exercises[0].completed_sets + exercise.superset.exercises[1].completed_sets}
                    /{exercise.superset.exercises[0].total_sets + exercise.superset.exercises[1].total_sets} sets
                  </span>
                </div>

                {/* Both exercises */}
                <div className="flex flex-col gap-2 mb-3 flex-1 overflow-y-auto">
                  {exercise.superset.exercises.map((ssEx) => (
                    renderExerciseMiniCard(ssEx, ssEx.is_active, workout)
                  ))}
                </div>
              </div>
            ) : (
              /* ===== SINGLE EXERCISE ===== */
              <>
                <div className="flex gap-3 mb-3">
                  <div className="flex-shrink-0 w-24 h-24 bg-black/30 rounded-xl overflow-hidden">
                    {exercise.exercise_gif ? (
                      <img src={getAnimationUrl(exercise.exercise_gif)} alt={exercise.exercise_name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><span className="text-4xl">{exercise.is_cardio ? '🏃' : '🏋️'}</span></div>
                    )}
                  </div>
                  <div className="flex-1 flex flex-col justify-center min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      {exercise.is_cardio && (
                        <span
                          className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 flex-shrink-0"
                          data-testid={`live-cardio-pill-${workout.workout_id}`}
                        >
                          🏃 Cardio
                        </span>
                      )}
                      {exercise.exercise_id ? (
                        <button
                          type="button"
                          onClick={() => setHistoryView({ exerciseId: exercise.exercise_id, exerciseName: exercise.exercise_name, clientId: workout.client_id, clientName: workout.client_name })}
                          className="text-white text-base font-bold leading-tight line-clamp-2 text-left hover:text-cyan-300 hover:underline decoration-dotted underline-offset-4 transition-colors cursor-pointer"
                          title="View this client's history for this exercise"
                          data-testid={`live-exercise-name-${workout.workout_id}`}
                        >
                          {exercise.exercise_name}
                        </button>
                      ) : (
                        <h3 className="text-white text-base font-bold leading-tight line-clamp-2">{exercise.exercise_name}</h3>
                      )}
                    </div>
                    <p className={`text-lg font-semibold ${exercise.is_cardio ? 'text-cyan-300' : 'text-blue-300'}`}>
                      {exercise.is_cardio ? 'Ronda' : 'Set'} {getCurrentSetProgress(exercise).current} / {getCurrentSetProgress(exercise).total}
                      {exercise.is_cardio && exercise.cardio_total_time > 0 && (
                        <span className="ml-2 text-cyan-200/70 text-sm font-mono">
                          · Total {formatTimeDuration(exercise.cardio_total_time)}
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Sets grid */}
                <div className="flex-1 overflow-y-auto mb-3">
                  <div className="grid grid-cols-2 gap-1.5">
                    {exercise.sets?.map((set, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center justify-between px-2 py-1.5 rounded-lg ${
                          set.status === 'current' ? 'bg-yellow-500/30 border-2 border-yellow-500'
                          : set.status === 'completed' ? (exercise.is_cardio ? 'bg-cyan-500/20 border border-cyan-500/40' : 'bg-green-500/20 border border-green-500/30')
                          : 'bg-gray-800/50 border border-gray-700/50'
                        }`}
                        data-testid={exercise.is_cardio ? `live-round-${idx}` : `live-set-${idx}`}
                      >
                        <div className="flex items-center gap-1.5">
                          {exercise.is_cardio ? (
                            <span className={`text-sm font-mono ${set.status === 'pending' ? 'text-gray-500' : 'text-white'}`}>
                              {set.status === 'current' && (!set.time || set.time === 0)
                                ? (liveCardioDisplay || 'En curso…')
                                : formatRoundDisplay(set)}
                            </span>
                          ) : (
                            <>
                              <span className={`text-sm font-mono ${set.status === 'pending' ? 'text-gray-500' : 'text-white'}`}>
                                {formatSetDisplay(set)}kg
                              </span>
                              {set.rpe != null && (
                                <span className={`text-xs font-bold px-1 py-0.5 rounded ${
                                  set.rpe <= 5 ? 'bg-green-600/40 text-green-300' 
                                  : set.rpe <= 7 ? 'bg-yellow-600/40 text-yellow-300' 
                                  : set.rpe <= 8 ? 'bg-orange-600/40 text-orange-300'
                                  : 'bg-red-600/40 text-red-300'
                                }`}>
                                  RPE {set.rpe}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        {renderSetIcon(set.status)}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* State / Rest Timer — aligned with intra-workout UX:
                ELAPSED stopwatch is the dominant display, target shown as a
                secondary "Objetivo" line. Progress bar grows 0→100. */}
            <div className="mb-3">
              {isResting ? (
                <div className={`${colors.headerBg} rounded-xl p-3 text-center`}>
                  <p className={`${colors.text} text-[11px] font-semibold uppercase tracking-widest`}>
                    {isSuperset ? '⏱️ Descansando (superset)' : '⏱️ Descansando'}
                  </p>
                  <p className="text-white text-3xl font-mono font-bold mt-1 tabular-nums" data-testid="live-rest-elapsed">
                    {formatRestTimer(workout.rest_timer)}
                  </p>
                  {formatRestTarget(workout.rest_timer) && (
                    <p className="text-white/60 text-xs mt-0.5" data-testid="live-rest-target">
                      Objetivo: <span className="tabular-nums font-medium">{formatRestTarget(workout.rest_timer)}</span>
                    </p>
                  )}
                  {workout.rest_timer?.progress !== undefined && renderProgressBar(workout.rest_timer.progress)}
                </div>
              ) : liveCardioDisplay ? (
                <div className="bg-cyan-900/50 border border-cyan-500/50 rounded-xl p-3 text-center" data-testid={`live-cardio-timer-${workout.workout_id}`}>
                  <p className="text-cyan-300 text-[11px] font-semibold uppercase tracking-widest">
                    🏃 En marcha
                  </p>
                  <p className="text-white text-3xl font-mono font-bold mt-1 tabular-nums animate-pulse" data-testid="live-cardio-elapsed">
                    {liveCardioDisplay}
                  </p>
                  {exercise?.target_time > 0 && (
                    <p className="text-white/60 text-xs mt-0.5">
                      Objetivo: <span className="tabular-nums font-medium">{formatTimeDuration(exercise.target_time)}</span>
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className={`px-3 py-1 rounded-full text-sm font-bold uppercase ${colors.bg} ${colors.text} border ${colors.border}`}>
                    {displayState}
                  </span>
                  {secondsSinceLastSet !== undefined && secondsSinceLastSet > 0 && (
                    <span className="text-gray-400 text-sm">
                      Last set: <span className="text-white font-mono">{formatTimeSince(secondsSinceLastSet)}</span> ago
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Next Exercise */}
            <div className="border-t border-gray-700/50 pt-2">
              {nextExercise ? (
                <p className="text-gray-400 text-sm">
                  <span className="text-gray-500">Next:</span>{' '}
                  <span className="text-blue-300">{nextExercise.exercise_name || nextExercise.name}</span>
                </p>
              ) : (
                <p className="text-gray-500 text-sm italic">Last exercise</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 mx-auto mb-4"></div>
          <p className="text-white text-xl">Loading Live Gym Board...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-red-900 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-2xl mb-4">{error}</p>
          <button onClick={fetchLiveBoard} className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">Retry</button>
        </div>
      </div>
    );
  }

  if (workouts.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center">
        <div className="text-center" data-testid="empty-state">
          <div className="text-8xl mb-6">🏋️</div>
          <h1 className="text-white text-4xl font-bold mb-4">No Active Workouts</h1>
          <p className="text-blue-300 text-xl">Workouts will appear here when clients start training</p>
          {lastUpdate && <p className="text-gray-500 text-sm mt-4">Last updated: {lastUpdate.toLocaleTimeString()}</p>}
        </div>
      </div>
    );
  }

  const getColumnWidth = () => {
    switch (workouts.length) {
      case 1: return 'max-w-md';
      case 2: return 'max-w-sm';
      case 3: return 'max-w-xs';
      case 4: return 'max-w-[280px]';
      case 5: return 'max-w-[260px]';
      default: return 'max-w-sm';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 p-4">
      {/* Header with clock */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-white text-2xl font-bold" data-testid="live-board-title">Live Gym Board</h1>
          <p className="text-blue-300 text-sm">
            {workouts.length} active workout{workouts.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Clock */}
        <div className="text-center" data-testid="live-board-clock">
          <p className="text-white text-3xl font-mono font-bold tracking-wide">
            {currentTime.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </p>
          <p className="text-gray-500 text-xs">
            {currentTime.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}
          </p>
        </div>

        <div className="text-right">
          <p className="text-gray-400 text-sm">Auto-refresh: 2s</p>
          {lastUpdate && <p className="text-gray-500 text-xs">{lastUpdate.toLocaleTimeString()}</p>}
        </div>
      </div>

      {/* Workout Columns */}
      <div className="flex justify-center gap-4 flex-wrap">
        {workouts.map((workout) => (
          <div key={workout.workout_id} className={`${getColumnWidth()} w-full`}>
            {renderColumn(workout)}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="fixed bottom-4 left-4 flex gap-4 text-sm bg-gray-900/80 px-4 py-2 rounded-lg">
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-green-500"></div><span className="text-green-400">READY</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-yellow-500"></div><span className="text-yellow-400">RESTING</span></div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-blue-500"></div><span className="text-blue-400">ACTIVE</span></div>
      </div>
      <div className="fixed bottom-4 right-4 flex gap-3 text-sm bg-gray-900/80 px-4 py-2 rounded-lg">
        <div className="flex items-center gap-1"><span className="text-green-400">✓</span><span className="text-gray-400">done</span></div>
        <div className="flex items-center gap-1"><span className="text-yellow-400">●</span><span className="text-gray-400">current</span></div>
        <div className="flex items-center gap-1"><span className="text-gray-500">○</span><span className="text-gray-400">pending</span></div>
      </div>

      {editing && (
        <LiveBoardCoachEditor
          workoutId={editing.workoutId}
          clientName={editing.clientName}
          onClose={() => setEditing(null)}
        />
      )}

      {historyView && (
        <ExerciseHistoryModal
          exerciseId={historyView.exerciseId}
          exerciseName={historyView.exerciseName}
          clientId={historyView.clientId}
          clientName={historyView.clientName}
          onClose={() => setHistoryView(null)}
        />
      )}
    </div>
  );
};

export default LiveGymBoard;
